/**
 * @format
 */

import React, { useMemo } from 'react';
import { Col, Row, Checkbox, Flex, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import Downtrend from 'img/ArrowFall.svg';
import Uptrend from 'img/ArrowRise.svg';
import Info from 'img/Info.svg';
import { SalesSummary as SalesSummaryType } from '../types/dashboard.types';
import { calculateAllTrends, formatCurrencyDisplay, formatPTCValue } from '../utils/calculationUtils';
import { Analytics, AnalyticsData } from 'lib/AnalyTics';

interface SalesSummaryProps {
  data: SalesSummaryType;
}

const SalesSummary: React.FC<SalesSummaryProps> = ({ data }) => {
  const [includePremium, setIncludePremium] = React.useState(false);

  // Get user role from Redux
  const userData = useSelector((state: RootState) => (state.user as any).userData);
  const userRole = userData?.role;
  const isAgent = userRole === 'agent';

  // Calculate trends
  const trends = useMemo(() => {
    return calculateAllTrends(data);
  }, [data]);

  // Initialize Analytics with sales summary data
  const analytics = useMemo(() => {
    const analyticsData: AnalyticsData = {
      totalAncillary: data.totalAncillaries,
      totalAncillaryPremium: data.totalAncillaryPremium,
      totalMajorMed: data.totalMajorMed,
      totalPrivateMed: data.totalPrivateMed,
      totalPrivateMedPremium: data.totalPrivateMedPremium,
      totalCost: data.totalCost,
      totalCalls: data.totalLeads,
      totalBillable: data.totalBillableLeads,
    };
    return new Analytics(analyticsData);
  }, [data]);

  const totalPremium = includePremium
    ? data.totalPremium + data.totalMajorMed * 50
    : data.totalPremium;

  const cpaA = analytics.cpaA();
  const cpaB = analytics.cpaB();
  
  // Calculate PTC based on whether Major Med premium is included
  const ptc = includePremium
    ? analytics.ptcB()  // PTC with Major Med included
    : analytics.ptcA(); // PTC without Major Med
    
  const closeRatio = Math.round(analytics.cr());

  return (
    <div className='dashboard-container'>
      <Row gutter={[28, 28]} className='rates'>
        {/* Total Marketing Cost */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-blue'>
            <div className='stats-title'>
              <span>Estimated Total Marketing Cost</span>
              <Tooltip title='Total marketing cost'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              {isAgent ? (
                <h2 style={{ fontSize: '14px', color: '#999' }}>Not available for you</h2>
              ) : (
                <>
                  <h2>{formatCurrencyDisplay(data.totalCost)}</h2>
                  <div>
                    <span>
                      {trends.totalSpentTrend.trend.toFixed(2)}%
                      <img
                        src={trends.totalSpentTrend.isPositive ? Uptrend : Downtrend}
                        alt={trends.totalSpentTrend.isPositive ? 'Uptrend' : 'Downtrend'}
                      />
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </Col>

        {/* P.T.C. */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div className='stats-title'>
              <span>P.T.C</span>
              <Tooltip title='Total Premium to Total Marketing cost'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{formatPTCValue(ptc)}</h2>
            </div>
          </div>
        </Col>

        {/* CPA-A */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div className='stats-title'>
              <span>CPA-A</span>
              <Tooltip title='Cost Per Acquisition without Major Med'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{formatCurrencyDisplay(cpaA)}</h2>
            </div>
          </div>
        </Col>

        {/* Q Calls */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-purple' style={{ height: '100%' }}>
            <div className='stats-title' style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
              <span>Q Calls</span>
              <div>
                <Flex justify='space-between' gap={5}>
                  <span>Total calls</span>
                  <Tooltip title='Total billable Queue calls'>
                    <img src={Info} alt='info icon' />
                  </Tooltip>
                </Flex>
              </div>
            </div>
            <div className='stats' style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              {isAgent ? (
                <h2 style={{ fontSize: '14px', color: '#999', width: '100%', textAlign: 'center' }}>Not available for you</h2>
              ) : (
                <>
                  <h2>{data.totalBillableLeads}</h2>
                  <h2>{data.totalLeads}</h2>
                </>
              )}
            </div>
          </div>
        </Col>

        {/* CPA-B */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div className='stats-title'>
              <span>CPA-B</span>
              <Tooltip title='Cost Per Acquisition with Major Med'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              {isAgent ? (
                <h2 style={{ fontSize: '14px', color: '#999' }}>Not available for you</h2>
              ) : (
                <h2>{formatCurrencyDisplay(cpaB)}</h2>
              )}
            </div>
          </div>
        </Col>

        {/* Total Premium */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div
              className='stats-title'
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
              }}>
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                }}>
                <span>Total Premium</span>
                <Tooltip title='Total premium click the check mark to include ACA sales'>
                  <img src={Info} alt='info icon' style={{ width: '16px' }} />
                </Tooltip>
              </div>
              <Checkbox
                checked={includePremium}
                onChange={(e) => setIncludePremium(e.target.checked)}
                style={{ marginLeft: '10px' }}
              />
            </div>
            <div className='stats'>
              <h2>{formatCurrencyDisplay(totalPremium)}</h2>
              <div>
                <span>
                  {trends.majorMedTrend.trend.toFixed(2)}%
                  <img
                    src={trends.majorMedTrend.isPositive ? Uptrend : Downtrend}
                    alt={trends.majorMedTrend.isPositive ? 'Uptrend' : 'Downtrend'}
                  />
                </span>
              </div>
            </div>
          </div>
        </Col>

        {/* ACA Core */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div className='stats-title'>
              <span>ACA Core</span>
              <Tooltip title='Total Major Med sold'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{data.totalMajorMed}</h2>
              <div>
                <span>
                  {trends.majorMedTrend.trend.toFixed(2)}%
                  <img
                    src={trends.majorMedTrend.isPositive ? Uptrend : Downtrend}
                    alt={trends.majorMedTrend.isPositive ? 'Uptrend' : 'Downtrend'}
                  />
                </span>
              </div>
            </div>
          </div>
        </Col>

        {/* Close Ratio */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-purple' style={{ backgroundColor: '#baedbd', height: '100%' }}>
            <div className='stats-title'>
              <span>Close Ratio</span>
              <Tooltip title='Close Ratio'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{closeRatio}%</h2>
            </div>
          </div>
        </Col>

        {/* U65 Core */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div className='stats-title'>
              <span>U65 Core</span>
              <Tooltip title='Total Private Med sold'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{data.totalPrivateMed}</h2>
              <div>
                <span>
                  {trends.privateMedTrend.trend.toFixed(2)}%
                  <img
                    src={trends.privateMedTrend.isPositive ? Uptrend : Downtrend}
                    alt={trends.privateMedTrend.isPositive ? 'Uptrend' : 'Downtrend'}
                  />
                </span>
              </div>
            </div>
          </div>
        </Col>

        {/* Ancillaries */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div className='stats-title'>
              <span>Ancillaries</span>
              <Tooltip title='Total Ancillary Sold'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{data.totalAncillaries}</h2>
              <div>
                <span>
                  {trends.ancillariesTrend.trend.toFixed(2)}%
                  <img
                    src={trends.ancillariesTrend.isPositive ? Uptrend : Downtrend}
                    alt={trends.ancillariesTrend.isPositive ? 'Uptrend' : 'Downtrend'}
                  />
                </span>
              </div>
            </div>
          </div>
        </Col>

        {/* Total Post Dates */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-light'>
            <div className='stats-title'>
              <span>Total Post Dates</span>
              <Tooltip title='Total Post Date sales'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{data.totalPost}</h2>
            </div>
          </div>
        </Col>

        {/* Total Sales */}
        <Col xs={12} sm={8} md={8} lg={6} xl={6}>
          <div className='rates-purple' style={{ backgroundColor: '#ffe999', height: '100%' }}>
            <div className='stats-title'>
              <span>Sales</span>
              <Tooltip title='Total sales'>
                <img src={Info} alt='info icon' />
              </Tooltip>
            </div>
            <div className='stats'>
              <h2>{data.totalSales}</h2>
              <div>
                <span>
                  {trends.totalSalesTrend.trend.toFixed(2)}%
                  <img
                    src={trends.totalSalesTrend.isPositive ? Uptrend : Downtrend}
                    alt={trends.totalSalesTrend.isPositive ? 'Uptrend' : 'Downtrend'}
                  />
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SalesSummary;
